import { CUSTOM_FIELD_PREFIX } from "@/shared/domain/boards/entityFieldNames";
import {
  INTERACTION_ENTITY_TYPES,
  type InteractionEntityType,
} from "@/shared/domain/boards/interactionTypes";
import {
  entityRecordColumns,
  type EntityRecordValueKind,
} from "@/shared/domain/query/entityRecordColumns";
import { roboqlFields } from "@/shared/domain/query/roboqlFields";
import { NANOSECOND_TIMESTAMP_KEYS } from "@/shared/json/parseTimestampsAsBigInt";

/**
 * The columns an `entity.query` source's rows result has, and their types,
 * for each target. They follow from the record model the query service
 * returns for the target (`entityRecordColumns`, generated from the Python
 * models) and from the RoboQL catalog, never from the rows a query returns,
 * so a result has the same columns and types whatever rows it holds, even
 * when it holds none.
 */

/** The targets whose records a board can read. */
export type EntityTarget = keyof typeof entityRecordColumns;

/** The entity catalogs a query may search and a field control may offer
 *  the fields of: a subset of the platform's QueryTarget enum. */
export const ENTITY_QUERY_TARGETS = [
  "events",
  "sessions",
  "datasets",
  "collections",
  "files",
  "devices",
] as const;

export type EntityQueryTarget = (typeof ENTITY_QUERY_TARGETS)[number];

/** Whether `target` names one of the entity catalogs
 *  (`ENTITY_QUERY_TARGETS`). */
export function isEntityQueryTarget(
  target: string,
): target is EntityQueryTarget {
  return (ENTITY_QUERY_TARGETS as readonly string[]).includes(target);
}

/** An entity's name as a sentence uses it in the singular. */
export const ENTITY_NOUNS: Record<EntityTarget, string> = {
  sessions: "session",
  events: "event",
  datasets: "dataset",
  files: "file",
  devices: "device",
  collections: "collection",
};

/** The DuckDB type of an entity result column. Every instant is a
 *  `TIMESTAMP_MS`, every number a `DOUBLE`, and every object or list
 *  `VARCHAR` holding its JSON text. */
export type EntityColumnType =
  | "BOOLEAN"
  | "DOUBLE"
  | "TIMESTAMP_MS"
  | "VARCHAR";

/** Where a column's value comes from in one returned record. */
export type EntityColumnSource =
  /** The value at a path of keys into the record. */
  | { kind: "path"; path: readonly string[] }
  /** The nanoseconds from the instant at `earlier` to the one at `later`,
   *  two top-level keys holding nanosecond timestamps. */
  | { kind: "difference"; later: string; earlier: string }
  /** The record's name, or its id when the name is empty or missing. */
  | { kind: "name"; nameKey: string; idKey: string };

export interface EntityColumn {
  name: string;
  type: EntityColumnType;
  source: EntityColumnSource;
}

/**
 * The columns that identify one row of an entity target: the id column,
 * spelled as the record names it, and, for a target whose record has a
 * name, a `<entity>_name` column read from `name.field`. The id is the
 * column a `focus` selection clause on an entity is matched on and the
 * column joins use; the name is what legends and tables show.
 */
export interface EntityIdentityColumns {
  idColumn: string;
  /** The name column and the record key it reads. Absent for a target
   *  whose record carries no name (devices). */
  name?: { column: string; field: string };
}

/**
 * The identity columns of sessions, under the names every table that
 * carries a session gives them: a sessions result and a topic result scoped
 * to sessions.
 */
export const SESSION_IDENTITY = {
  idColumn: "session_id",
  name: { column: "session_name", field: "name" },
} as const;

/** A top-level key of the record the query service returns for `Target`. */
type EntityRecordKey<Target extends EntityTarget> =
  keyof (typeof entityRecordColumns)[Target];

/** The identity columns per target. Every rows result carries them, whether
 *  or not the query declared them. Devices carry an id alone. Each id and
 *  name field is a key of the target's record, so a record model that
 *  renames one fails the type check. */
export const ENTITY_IDENTITY_BY_TARGET: Record<
  EntityTarget,
  EntityIdentityColumns
> = {
  collections: {
    idColumn: "collection_id",
    name: { column: "collection_name", field: "name" },
  },
  datasets: {
    idColumn: "dataset_id",
    name: { column: "dataset_name", field: "name" },
  },
  devices: { idColumn: "device_id" },
  events: {
    idColumn: "event_id",
    name: { column: "event_name", field: "name" },
  },
  files: { idColumn: "file_id", name: { column: "file_name", field: "name" } },
  sessions: SESSION_IDENTITY,
} satisfies {
  [Target in EntityTarget]: {
    idColumn: EntityRecordKey<Target>;
    name?: { column: string; field: EntityRecordKey<Target> };
  };
};

/** The record key under which a returned record carries its custom-field
 *  values, keyed by field name. */
export const CUSTOM_FIELDS_RECORD_KEY = "custom_fields";

/** The targets whose record carries custom-field values. */
export type CustomFieldTarget = {
  [Target in EntityTarget]: typeof CUSTOM_FIELDS_RECORD_KEY extends EntityRecordKey<Target>
    ? Target
    : never;
}[EntityTarget];

/**
 * The columns of a rows result that declares no `fields`, in order:
 *
 * 1. each top-level key of the target's record, in the record model's
 *    order (`recordColumns`);
 * 2. each catalog field the RoboQL catalog computes as the difference of
 *    two of the record's nanosecond timestamps, such as a session's
 *    `duration` (`differenceColumns`);
 * 3. the target's name column, such as `session_name`, for a target whose
 *    record has a name (`identityColumns`).
 */
export function entityColumns(target: EntityTarget): readonly EntityColumn[] {
  const columns = [...recordColumns(target), ...differenceColumns(target)];
  const names = new Set(columns.map((column) => column.name));
  return [
    ...columns,
    ...identityColumns(target).filter((column) => !names.has(column.name)),
  ];
}

/**
 * The identity columns of a target's rows, each as a column built from the
 * record: the id, then the name, which falls back to the id.
 */
export function identityColumns(target: EntityTarget): EntityColumn[] {
  const identity = ENTITY_IDENTITY_BY_TARGET[target];
  const columns: EntityColumn[] = [
    {
      name: identity.idColumn,
      type: "VARCHAR",
      source: { kind: "path", path: [identity.idColumn] },
    },
  ];
  if (identity.name !== undefined) {
    columns.push({
      name: identity.name.column,
      type: "VARCHAR",
      source: {
        kind: "name",
        nameKey: identity.name.field,
        idKey: identity.idColumn,
      },
    });
  }
  return columns;
}

/**
 * What a name in a query's `fields` makes of the result:
 *
 * - `column`: one of the target's columns (`entityColumns`), or a RoboQL
 *   catalog alias of one (`start_time` on sessions), which gives that
 *   column's values and type under the alias the query declared;
 * - `custom`: the custom field `custom.<name>` of an entity with custom
 *   fields, typed by the org's definition of it when the query runs;
 * - `refused`: none of these, with the sentence that says why and what the
 *   target offers instead.
 */
export type DeclaredField =
  | { kind: "column"; column: EntityColumn }
  | { kind: "custom"; name: string }
  | { kind: "refused"; message: string };

export function declaredField(
  target: EntityTarget,
  field: string,
): DeclaredField {
  const column = entityColumns(target).find(
    (candidate) => candidate.name === field,
  );
  if (column !== undefined) {
    return { kind: "column", column };
  }
  const aliased = columnOfAlias(target, field);
  if (aliased !== undefined) {
    return { kind: "column", column: { ...aliased, name: field } };
  }
  if (
    field.startsWith(CUSTOM_FIELD_PREFIX) &&
    hasRecordKey(target, CUSTOM_FIELDS_RECORD_KEY) &&
    field.length > CUSTOM_FIELD_PREFIX.length
  ) {
    return { kind: "custom", name: field.slice(CUSTOM_FIELD_PREFIX.length) };
  }
  return { kind: "refused", message: fieldRefusal(target, field) };
}

/** How the target's record carries each of its top-level keys. */
function recordKinds(
  target: EntityTarget,
): Readonly<Record<string, EntityRecordValueKind>> {
  return entityRecordColumns[target];
}

function hasRecordKey(target: EntityTarget, key: string): boolean {
  return Object.hasOwn(recordKinds(target), key);
}

function recordColumns(target: EntityTarget): EntityColumn[] {
  return Object.entries(recordKinds(target)).map(([key, kind]) => ({
    name: key,
    type: columnTypeOf(key, kind),
    source: { kind: "path", path: [key] },
  }));
}

/**
 * The column type of a record value: a date-time string and an integer
 * under a nanosecond timestamp key (`NANOSECOND_TIMESTAMP_KEYS`, which the
 * results parse reads as a `bigint`) are instants; every other number is a
 * `DOUBLE`; an object or list is its JSON text.
 */
function columnTypeOf(
  key: string,
  kind: EntityRecordValueKind,
): EntityColumnType {
  switch (kind) {
    case "boolean":
      return "BOOLEAN";
    case "date-time":
      return "TIMESTAMP_MS";
    case "integer":
      return NANOSECOND_TIMESTAMP_KEYS.has(key) ? "TIMESTAMP_MS" : "DOUBLE";
    case "number":
      return "DOUBLE";
    case "array":
    case "object":
    case "string":
      return "VARCHAR";
  }
}

/** A catalog value expression that subtracts one record key from another,
 *  as the catalog writes it: `(max_timestamp_ns - min_timestamp_ns)`. */
const DIFFERENCE_EXPRESSION = /^\(?(\w+) - (\w+)\)?$/;

/**
 * The catalog fields of `target` that are no record key and whose catalog
 * value is the difference of two of the record's nanosecond timestamps, as
 * `DOUBLE` columns of nanoseconds: `duration` on sessions and events. The
 * service filters, sorts and groups by the same value.
 */
function differenceColumns(target: EntityTarget): EntityColumn[] {
  const isNanosecondTimestamp = (key: string) =>
    recordKinds(target)[key] === "integer" &&
    NANOSECOND_TIMESTAMP_KEYS.has(key);
  const columns: EntityColumn[] = [];
  for (const field of roboqlFields[target] ?? []) {
    const operands = DIFFERENCE_EXPRESSION.exec(field.value_expression ?? "");
    if (operands === null || hasRecordKey(target, field.label)) {
      continue;
    }
    const [, later, earlier] = operands;
    if (isNanosecondTimestamp(later) && isNanosecondTimestamp(earlier)) {
      columns.push({
        name: field.label,
        type: "DOUBLE",
        source: { kind: "difference", later, earlier },
      });
    }
  }
  return columns;
}

/**
 * The column a catalog name stands for, when `name` is a label or alias of
 * a catalog field and that field's label or another of its aliases is a
 * column of the target: `start_time` on sessions is `min_timestamp_ns`, and
 * `dataset_id` on files is `association_id`. Undefined when no column
 * stands for it.
 */
function columnOfAlias(
  target: EntityTarget,
  name: string,
): EntityColumn | undefined {
  const field = (roboqlFields[target] ?? []).find(
    (candidate) => candidate.label === name || candidate.aliases.includes(name),
  );
  if (field === undefined) {
    return undefined;
  }
  const names = [field.label, ...field.aliases];
  return entityColumns(target).find((column) => names.includes(column.name));
}

function fieldRefusal(target: EntityTarget, field: string): string {
  const names = [
    ...entityColumns(target).map((column) => `"${column.name}"`),
    ...(hasRecordKey(target, CUSTOM_FIELDS_RECORD_KEY)
      ? [`a custom field as "${CUSTOM_FIELD_PREFIX}<name>"`]
      : []),
  ];
  const listed = `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`;
  return `"${field}" is not a column of a ${target} result. The "fields" of a ${target} query take ${listed}. They also take the name a filter uses for one of these columns, such as "id".`;
}

/** The kind of entity a column holds the ids of, going by its name. */
export function entityOfIdColumn(
  column: string,
): InteractionEntityType | undefined {
  return INTERACTION_ENTITY_TYPES.find(
    (entity) => ENTITY_IDENTITY_BY_TARGET[entity].idColumn === column,
  );
}
